/**
 * 账号切换组件
 * 在顶部导航栏左侧显示 Logo + 账号名称，支持多账号切换
 */
import { DownOutlined, CheckOutlined } from '@ant-design/icons';
import { useModel } from '@umijs/max';
import { Dropdown, Typography, App } from 'antd';
import type { MenuProps } from 'antd';
import { createStyles } from 'antd-style';
import React, { useCallback } from 'react';
import { switchAccount, TokenManager, type UserAccount } from '@/services/auth';

const { Text } = Typography;

/** Logo 地址 */
const LOGO_URL = 'https://ai.liteinn.com/logo.svg';

const useStyles = createStyles(({ token }) => ({
  container: {
    display: 'flex',
    alignItems: 'center',
    padding: '4px 12px',
    cursor: 'pointer',
    borderRadius: token.borderRadius,
    transition: 'background-color 0.2s',
    '&:hover': {
      backgroundColor: token.colorBgTextHover,
    },
  },
  logo: {
    width: 24,
    height: 24,
    marginRight: 8,
  },
  accountName: {
    fontSize: 14,
    fontWeight: 500,
    color: token.colorText,
  },
  dropdownIcon: {
    marginLeft: 6,
    fontSize: 10,
    color: token.colorTextSecondary,
  },
  menuItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  menuItemInfo: {
    flex: 1,
  },
  menuItemSelected: {
    color: token.colorPrimary,
  },
}));

const AccountSwitcher: React.FC = () => {
  const { styles } = useStyles();
  const { message } = App.useApp();
  const { initialState } = useModel('@@initialState');

  const currentUser = initialState?.currentUser;
  const accounts = currentUser?.accounts || [];
  const hasMultipleAccounts = accounts.length > 1;

  /**
   * 切换账号（刷新页面）
   */
  const handleSwitchAccount = useCallback(
    async (account: UserAccount) => {
      if (account.accountId === currentUser?.accountId) {
        return;
      }

      try {
        await switchAccount({ accountId: account.accountId });

        // 更新本地存储的 accountId
        TokenManager.setAccountId(account.accountId);

        message.success(`已切换到 ${account.accountName}`);

        // 刷新页面，重新加载数据
        window.location.reload();
      } catch (error: any) {
        message.error(error.message || '切换账号失败');
      }
    },
    [currentUser?.accountId, message],
  );

  const menuItems: MenuProps['items'] = accounts.map((account) => ({
    key: String(account.accountId),
    label: (
      <div className={styles.menuItem}>
        <div className={styles.menuItemInfo}>
          <Text strong>{account.accountName}</Text>
        </div>
        {account.accountId === currentUser?.accountId && (
          <CheckOutlined className={styles.menuItemSelected} />
        )}
      </div>
    ),
    onClick: () => handleSwitchAccount(account),
  }));

  const menuProps: MenuProps = {
    items: menuItems,
    selectedKeys: currentUser?.accountId ? [String(currentUser.accountId)] : [],
  };

  // 如果没有用户信息，只显示 Logo
  if (!currentUser) {
    return (
      <div
        className={styles.container}
        style={{ cursor: 'default' }}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <img src={LOGO_URL} alt="logo" className={styles.logo} />
      </div>
    );
  }

  const accountName = currentUser.accountName || '未选择账号';

  // 如果只有一个账号，不显示下拉箭头，但仍可点击展示账号列表
  return (
    <Dropdown menu={menuProps} placement="bottomLeft" trigger={['click']}>
      <div className={styles.container}>
        <img src={LOGO_URL} alt="logo" className={styles.logo} />
        <span className={styles.accountName}>{accountName}</span>
        {hasMultipleAccounts && <DownOutlined className={styles.dropdownIcon} />}
      </div>
    </Dropdown>
  );
};

export default AccountSwitcher;
